import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const CreditAlertContext = createContext(null);

export const useCreditAlert = () => {
  const context = useContext(CreditAlertContext);
  if (!context) {
    throw new Error('useCreditAlert must be used within a CreditAlertProvider');
  }
  return context;
};

function parseSseEventBlock(rawBlock) {
  const lines = String(rawBlock || '').split(/\r?\n/);
  let event = 'message';
  const dataLines = [];

  lines.forEach((rawLine) => {
    const line = String(rawLine || '');
    if (!line || line.startsWith(':')) return;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim() || 'message';
      return;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  });

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

function consumeSseBuffer(buffer, onEvent) {
  const blocks = String(buffer || '').split(/\r?\n\r?\n/);
  const remainder = blocks.pop() ?? '';
  blocks.forEach((block) => {
    const parsedEvent = parseSseEventBlock(block);
    if (parsedEvent) onEvent(parsedEvent);
  });
  return remainder;
}

export const CreditAlertProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [creditAlert, setCreditAlert] = useState(null);
  const dismissTimerRef = useRef(null);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const dismissCreditAlert = useCallback(() => {
    clearDismissTimer();
    setCreditAlert(null);
  }, [clearDismissTimer]);

  const showCreditAlert = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    clearDismissTimer();
    setCreditAlert(payload);
    dismissTimerRef.current = window.setTimeout(() => {
      setCreditAlert(null);
      dismissTimerRef.current = null;
    }, 20000);
  }, [clearDismissTimer]);

  useEffect(() => {
    if (!token || user?.role === 'viewer') {
      dismissCreditAlert();
      return undefined;
    }

    let isClosed = false;
    const controller = new AbortController();
    const decoder = new TextDecoder();
    const apiUrl = process.env.REACT_APP_BACKEND_URL;

    const handleSseEvent = (parsedEvent) => {
      if (parsedEvent?.event !== 'credit_alert' || !parsedEvent.data) return;
      try {
        showCreditAlert(JSON.parse(parsedEvent.data));
      } catch (error) {
        // Ignore malformed stream events; the next valid event will recover the banner.
      }
    };

    const connect = async () => {
      let buffer = '';
      try {
        const response = await fetch(`${apiUrl}/api/v2/credit-alerts/stream`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
          cache: 'no-store',
        });

        if (!response.ok || !response.body) return;

        const reader = response.body.getReader();
        while (!isClosed) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          buffer = consumeSseBuffer(buffer, handleSseEvent);
        }
      } catch (error) {
        // The banner stream is best-effort; losing it should not interrupt dashboard use.
      }
    };

    void connect();

    return () => {
      isClosed = true;
      controller.abort();
    };
  }, [dismissCreditAlert, showCreditAlert, token, user?.role]);

  useEffect(() => () => clearDismissTimer(), [clearDismissTimer]);

  return (
    <CreditAlertContext.Provider value={{ creditAlert, dismissCreditAlert }}>
      {children}
    </CreditAlertContext.Provider>
  );
};
